import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert, FormFeedback } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { verify2FA, clearError, clearVerificationRequirement, setVerificationHeaders } from '../../slices/two-factor.slice';
import { getDeviceName } from '../../api';
import './TwoFactorVerification.scss';

const TwoFactorVerification = ({ isOpen, onVerified, onCancel, purpose = 'access' }) => {
    const dispatch = useDispatch();
    const { isLoading, error, requiresVerification, verificationFor } = useSelector(state => state.twoFactor);
    const [code, setCode] = useState('');
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [trustDevice, setTrustDevice] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [deviceId] = useState(() => {
        let id = localStorage.getItem('deviceId');
        if (!id) {
            id = uuidv4();
            localStorage.setItem('deviceId', id);
        }
        return id;
    });

    useEffect(() => {
        if (isOpen) {
            setCode('');
            setUseBackupCode(false);
            setTrustDevice(false);
            setAttempts(0);
            dispatch(clearError());
        }
    }, [isOpen, dispatch]);

    const handleVerify = async (e) => {
        e.preventDefault();
        
        if (!code.trim()) return;
        
        try {
            const verificationData = {
                twoFactorCode: code.trim(),
                isBackupCode: useBackupCode,
                deviceId: trustDevice ? deviceId : undefined,
                deviceName: trustDevice ? getDeviceName() : undefined,
                trustDevice: trustDevice
            };
            
            if (purpose === 'login') {
                // For login, pass data to parent component
                onVerified && onVerified(verificationData);
            } else {
                // For other actions, use the 2FA verification API
                await dispatch(verify2FA(verificationData)).unwrap();
                
                const timestamp = Date.now().toString();
                dispatch(setVerificationHeaders(timestamp));
                
                localStorage.setItem('2fa-verified', 'true');
                localStorage.setItem('2fa-timestamp', timestamp);
                
                dispatch(clearVerificationRequirement());
                onVerified && onVerified();
            }
        } catch (error) {
            setAttempts(prev => prev + 1);
            setCode('');
        }
    };

    const handleCancel = () => {
        dispatch(clearError());
        dispatch(clearVerificationRequirement());
        setCode('');
        setUseBackupCode(false);
        setTrustDevice(false);
        setAttempts(0);
        onCancel && onCancel();
    };

    const getPurposeMessage = () => {
        if (verificationFor) return verificationFor;
        
        switch (purpose) {
            case 'password-change':
                return 'changing your password';
            case 'email-change':
                return 'changing your email address';
            case 'account-deletion':
                return 'deleting your account';
            case 'financial-transaction':
                return 'processing this financial transaction';
            case 'security-settings':
                return 'modifying security settings';
            default:
                return 'completing this action';
        }
    };

    const shouldShowModal = isOpen || requiresVerification;

    return (
        <Modal isOpen={shouldShowModal} toggle={handleCancel} className="two-factor-verification">
            <ModalHeader toggle={handleCancel}>
                Two-Factor Authentication Required
            </ModalHeader>
            <ModalBody>
                <div className="verification-header">
                    <div className="shield-icon">
                        <i className="fas fa-shield-alt"></i>
                    </div>
                    <p className="text-muted">
                        Please verify your identity before {getPurposeMessage()}.
                    </p>
                </div>

                {error && (
                    <Alert color="danger" className="mb-3">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                <Form onSubmit={handleVerify} className="verification-form">
                    {!useBackupCode ? (
                        <FormGroup>
                            <Label for="authCode">
                                Enter the 6-digit code from your authenticator app:
                            </Label>
                            <Input
                                type="text"
                                id="authCode"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                maxLength={6}
                                className="verification-input"
                                invalid={!!error && attempts > 0}
                                required
                                autoFocus
                            />
                            {error && attempts > 0 && (
                                <FormFeedback>
                                    Invalid verification code
                                </FormFeedback>
                            )}
                        </FormGroup>
                    ) : (
                        <FormGroup>
                            <Label for="backupCode">
                                Enter one of your backup codes:
                            </Label>
                            <Input
                                type="text"
                                id="backupCode"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9))}
                                placeholder="XXXX-XXXX"
                                maxLength={9}
                                className="backup-code-input"
                                invalid={!!error && attempts > 0}
                                required
                                autoFocus
                            />
                            {error && attempts > 0 && (
                                <FormFeedback>
                                    Invalid backup code
                                </FormFeedback>
                            )}
                        </FormGroup>
                    )}

                    <div className="trust-device-section">
                        <div className="form-check">
                            <Input
                                type="checkbox"
                                id="trustDevice"
                                checked={trustDevice}
                                onChange={(e) => setTrustDevice(e.target.checked)}
                                className="form-check-input"
                            />
                            <Label for="trustDevice" className="form-check-label">
                                Trust this device for 60 days
                            </Label>
                        </div>
                        <small className="text-muted">
                            You won't be asked for 2FA on this device for 60 days.
                        </small>
                    </div>

                    {attempts > 0 && (
                        <div className="verification-attempts">
                            Failed attempts: {attempts}/5
                        </div>
                    )}
                </Form>

                <div className="backup-code-section">
                    <button
                        type="button"
                        className="btn btn-link backup-code-toggle p-0"
                        onClick={() => {
                            setUseBackupCode(!useBackupCode);
                            setCode('');
                            dispatch(clearError());
                        }}
                    >
                        {useBackupCode ? (
                            <>
                                <i className="fas fa-mobile-alt me-1"></i>
                                Use authenticator app instead
                            </>
                        ) : (
                            <>
                                <i className="fas fa-key me-1"></i>
                                Use a backup code instead
                            </>
                        )}
                    </button>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button 
                    color="primary" 
                    onClick={handleVerify} 
                    disabled={isLoading || !code.trim() || (useBackupCode ? code.length < 8 : code.length !== 6)}
                >
                    {isLoading ? (
                        <>
                            <span className="loading-spinner me-2"></span>
                            Verifying...
                        </>
                    ) : (
                        'Verify'
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default TwoFactorVerification;
